import { useState, useEffect, useMemo, useCallback } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { format, isAfter, parseISO } from "date-fns"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Progress } from "@/components/ui/progress"
import { Separator } from "@/components/ui/separator"
import { 
  Plus,
  Search,
  Filter,
  Eye,
  MessageSquare,
  FileText,
  AlertCircle,
  CheckCircle2,
  Clock,
  TrendingUp,
  TrendingDown,
  Activity,
  PoundSterling,
  Zap,
  X,
  Settings,
  Loader2,
  FileSpreadsheet,
  User,
  Building2,
  Mail,
  Phone,
  AlertTriangle,
  DollarSign,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  UserCheck,
  GitBranch,
  Pencil,
  Receipt,
  Trash2,
  StickyNote,
  UserCog,
  Flag,
  XCircle,
  RotateCcw,
  ChevronDown,
} from "lucide-react"
import { ColumnConfigDialog, type ColumnDefinition } from "@/components/shared/column-config-dialog"
import { useScreenColumnPreferences } from "@/hooks/useScreenColumnPreferences"
import { UniqueTableNamesForColumnConfiguration } from "@/config/unique-table-names-for-column-configuration"
import { useCreateDispute, useUpdateDispute, useQuickUpdateDisputes, useDisputeTypeOptions, useUserOptions, useDisputePriorities } from "@/hooks/useDisputesData"
import type { GetDisputesParams } from "@/hooks/useDisputesData"
import { useDisputesPageDataGraphQL } from "@/hooks/useDisputesGraphQL"
import type { DisputeUI } from "@/types/ui/disputes"
import type { CreateDisputeRequest, UpdateDisputeRequest } from "@/types/api/disputes"
import { FeatureFeedbackCollector } from "@/components/FeedbackCollector"
import { LogCallModal, SendEmailModal, AddNoteModal } from "@/components/collections/action-modals"
import { exportDisputesToExcel } from "@/lib/export-utils"
import { cn } from "@/lib/utils"
import { useUsersList } from "@/hooks/useUsersData"
import { useCustomerContactsList } from "@/hooks/useCustomerContacts"
import { getWorkflowForDisputeType } from "@/lib/utils/dispute-workflow-mapper"
import { getTotalLevelsForDisputeType } from "@/lib/utils/dispute-workflow-utils"
import { ResolveDisputeModal } from "@/components/disputes/resolve-dispute-modal"
import { ConsolidatedActivityTimeline } from "@/components/collections/consolidated-activity-timeline"
import type { CollectionActivity, CollectionCommunication, CaseNote } from "@/types/collections"
import { useAddDisputeNote } from "@/hooks/useDisputeNotes"
import { useDebouncedValue } from "@/hooks/useDebouncedValue"
import { getPageNumbers } from "@/lib/pagination-utils"

const DEFAULT_DISPUTE_COLUMNS: ColumnDefinition[] = [
  { id: 'disputeId', label: 'Dispute ID', visible: true, order: 0 },
  { id: 'customer', label: 'Customer', visible: true, order: 1 },
  { id: 'type', label: 'Type', visible: true, order: 2 },
  { id: 'amount', label: 'Amount', visible: true, order: 3 },
  { id: 'status', label: 'Status', visible: true, order: 4 },
  { id: 'priority', label: 'Priority', visible: true, order: 5 },
  { id: 'workflowProgress', label: 'Workflow Progress', visible: true, order: 6 },
  { id: 'assignedTo', label: 'Assigned To', visible: true, order: 7 },
  { id: 'dueDate', label: 'Due Date', visible: true, order: 8 },
  { id: 'createdDate', label: 'Created Date', visible: false, order: 9 },
  { id: 'workflow', label: 'Workflow', visible: false, order: 10 },
  { id: 'description', label: 'Description', visible: false, order: 11 },
];

const getContactPlaceholder = (customerId: string, contactCount: number) => {
  if (!customerId) return "Select customer first";
  if (contactCount > 0) return "Select contact";
  return "No contacts available";
};

const statusColors = {
  "New": "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  "Under Review": "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300",
  "In Progress": "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300",
  "Escalated": "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
  "Resolved": "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
  "Closed": "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300",
}

const priorityColors = {
  "Low": "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300",
  "Medium": "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  "High": "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300",
  "Critical": "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
}

// Mock data for Activity Timeline
const mockActivities: CollectionActivity[] = [
  {
    id: "act-1",
    caseId: "DSP-001",
    type: "status_change",
    description: "Dispute status changed from 'New' to 'In Progress'",
    outcome: "Status updated successfully",
    createdBy: "System",
    createdAt: new Date("2024-01-15T09:00:00"),
  },
  {
    id: "act-2",
    caseId: "DSP-001",
    type: "call",
    description: "Called customer to discuss dispute details",
    outcome: "Customer provided additional documentation",
    createdBy: "John Smith",
    createdAt: new Date("2024-01-16T10:30:00"),
  },
]

const mockCommunications: CollectionCommunication[] = [
  {
    id: "comm-1",
    caseId: "DSP-001",
    type: "email",
    direction: "outbound",
    subject: "Dispute Investigation - Additional Information Required",
    content: "We are investigating your dispute and require additional documentation to proceed.",
    sentBy: "Sarah Johnson",
    sentAt: new Date("2024-01-15T14:15:00"),
    status: "sent",
  },
  {
    id: "comm-2",
    caseId: "DSP-001",
    type: "email",
    direction: "inbound",
    subject: "RE: Dispute Investigation",
    content: "Please find attached the requested documentation for your review.",
    sentBy: "Customer",
    sentAt: new Date("2024-01-16T09:00:00"),
    status: "delivered",
  },
]

const mockNotes: CaseNote[] = [
  {
    id: "note-1",
    caseId: "DSP-001",
    content: "Contacted customer to verify service dates. Awaiting response.",
    createdBy: "John Smith",
    createdAt: new Date("2024-01-16T10:30:00"),
  },
]

const documents = [
  { name: "Original Invoice.pdf", size: "245 KB", date: "2024-01-15" },
  { name: "Service Agreement.pdf", size: "1.2 MB", date: "2024-01-15" },
  { name: "Customer Email.msg", size: "45 KB", date: "2024-01-16" },
]

const initialCreateFormState = {
  customerId: "",
  contactId: "",
  contactEmail: "",
  contactPhone: "",
  disputeTypeId: "",
  amount: "",
  statusId: "1",
  priorityId: "",
  assignedToUserId: "",
  dueDate: "",
  description: "",
}


function UpdatePriorityModal({
  open,
  onOpenChange,
  selectedCount,
  selectedDisputeId,
  priorities,
  value,
  onValueChange,
  onConfirm,
}: Readonly<{
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedCount: number
  selectedDisputeId: string | undefined
  priorities: Array<{ code: string; name: string; id?: number }>
  value: string
  onValueChange: (value: string) => void
  onConfirm: () => void
}>) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flag className="h-5 w-5 text-primary" />
            Update Priority
          </DialogTitle>
          <DialogDescription>
            {selectedCount === 1
              ? <>Change the priority for dispute <span className="font-semibold">{selectedDisputeId}</span></>
              : <>Update the priority for <span className="font-semibold">{selectedCount} disputes</span></>
            }
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <Label>New Priority</Label>
          <Select value={value} onValueChange={onValueChange}>
            <SelectTrigger>
              <SelectValue placeholder="Select priority..." />
            </SelectTrigger>
            <SelectContent>
              {priorities.map((p) => (
                <SelectItem key={p.code} value={p.name}>
                  <div className="flex items-center gap-2">
                    <Badge className={priorityColors[p.name as keyof typeof priorityColors]}>{p.name}</Badge>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onConfirm} disabled={!value} className="gap-2">
            <Flag className="h-4 w-4" />
            {selectedCount > 1 ? `Update ${selectedCount} Disputes` : "Update Priority"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default function DisputesAllPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("")
  const debouncedSearch = useDebouncedValue(searchQuery)
  const [statusFilter, setStatusFilter] = useState("all")
  const [priorityFilter, setPriorityFilter] = useState("all")
  const [assignedUserFilter, setAssignedUserFilter] = useState("all")
  
  // Server-side sorting state
  const [sortBy, setSortBy] = useState<string>("createdAt")
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc")
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [selectedDispute, setSelectedDispute] = useState<DisputeUI | null>(null)
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false)
  // Multi-select: array of selected dispute IDs
  const [selectedDisputeIds, setSelectedDisputeIds] = useState<string[]>([])
  const [isColumnConfigOpen, setIsColumnConfigOpen] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [logCallOpen, setLogCallOpen] = useState(false)
  const [sendEmailOpen, setSendEmailOpen] = useState(false)

  // Quick action modal states
  const [addNoteOpen, setAddNoteOpen] = useState(false)
  const [reassignOpen, setReassignOpen] = useState(false)
  const [reassignUserId, setReassignUserId] = useState("")
  const [updatePriorityOpen, setUpdatePriorityOpen] = useState(false)
  const [updatePriorityValue, setUpdatePriorityValue] = useState("")
  const [closeDisputeOpen, setCloseDisputeOpen] = useState(false)
  const [closeReason, setCloseReason] = useState<'duplicate' | 'customer_withdrew' | 'invalid_dispute' | 'other'>('duplicate')
  const [closeNote, setCloseNote] = useState('')
  const [resolveDisputeOpen, setResolveDisputeOpen] = useState(false)
  const [reopenDisputeOpen, setReopenDisputeOpen] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [createForm, setCreateForm] = useState(initialCreateFormState)
  const [createFormContacts, setCreateFormContacts] = useState<Array<{ id: string; name: string; email: string; phone: string }>>([])
  
  // Next Action Modal state
  const [isNextActionModalOpen, setIsNextActionModalOpen] = useState(false)
  const [nextActionNotes, setNextActionNotes] = useState('')
  const [nextActionFollowUpDate, setNextActionFollowUpDate] = useState('')
  
  // Edit mode state for dispute fields
  const [editedDispute, setEditedDispute] = useState<Partial<DisputeUI>>({})
  const [customerContacts, setCustomerContacts] = useState<Array<{ id: string; name: string; email: string; phone: string }>>([])
  
  // Transaction state
  const [editedTransactions, setEditedTransactions] = useState<Array<{
    id: string
    referenceNumber: string
    date: string
    dueDate: string
    description: string
    transactionType: import('@/types/transaction').TransactionType
    amount: number
    currency: string
    status: 'outstanding' | 'overdue' | 'completed'
  }>>([])
  
  // Activity tracking state
  const [activities, setActivities] = useState<CollectionActivity[]>(mockActivities)
  const [communications, setCommunications] = useState<CollectionCommunication[]>(mockCommunications)
  const [notes, setNotes] = useState<CaseNote[]>(mockNotes)
  
  const { 
    columns: disputeColumns,
    visibleColumns, 
    savePreferences: saveDisputeColumnPreferences,
    resetToDefaults: resetDisputeColumnPreferences,
  } = useScreenColumnPreferences(UniqueTableNamesForColumnConfiguration.DisputeManagementAllDisputes, DEFAULT_DISPUTE_COLUMNS)

  // Mode-aware contacts queries for selected dispute and create form
  const customerId = useMemo(() => selectedDispute?.customerId?.toString() || "0", [selectedDispute?.customerId])
  const { data: selectedDisputeContacts = [] } = useCustomerContactsList(customerId)
  
  const createFormCustomerId = useMemo(() => createForm.customerId || "0", [createForm.customerId])
  const { data: createFormContactsData = [] } = useCustomerContactsList(createFormCustomerId)

  // Reset to page 1 when filters or debounced search change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, assignedUserFilter]);

  // Build server-side query params
  const overdueOnly = searchParams.get('overdue') === 'true';
  const queryParams = useMemo<GetDisputesParams>(() => {
    const params: GetDisputesParams = {
      page: currentPage,
      pageSize,
      sortBy,
      sortOrder,
    };

    if (debouncedSearch) {
      params.search = debouncedSearch;
    }

    if (statusFilter !== "all") {
      params.status = statusFilter;
    }

    if (priorityFilter !== "all") {
      params.priority = priorityFilter;
    }

    if (assignedUserFilter !== "all") {
      const userId = Number(assignedUserFilter);
      if (!isNaN(userId)) params.assignedToUserId = userId;
    }

    return params;
  }, [currentPage, pageSize, sortBy, sortOrder, debouncedSearch, statusFilter, priorityFilter, assignedUserFilter]);

  const { disputes, pagination, totals, isLoading, refetch: refetchDisputes } = useDisputesPageDataGraphQL(queryParams);
  
  const { data: users = [] } = useUsersList()

  const activeUsers = useMemo(() =>
    (users || []).filter(user => user?.status === 'active'),
    [users]
  );

  const userFilterOptions = useMemo(() => {
    return activeUsers
      .map(user => ({
        id: user.id,
        displayName: `${user.firstName} ${user.lastName}`,
      }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [activeUsers]);
  const createDisputeMutation = useCreateDispute()
  const { data: disputeTypeOptions = [] } = useDisputeTypeOptions()
  const { data: userOptions = [] } = useUserOptions()
  const { data: disputePriorities = [] } = useDisputePriorities()
  
  const selectedDisputeNumericId = selectedDispute?.numericId ?? 0
  const updateDisputeMutation = useUpdateDispute(selectedDisputeNumericId)
  const quickUpdateDisputes = useQuickUpdateDisputes()
  const addDisputeNoteMutation = useAddDisputeNote()

  // Handle URL query parameters for filtering
  useEffect(() => {
    const status = searchParams.get('status');
    const priority = searchParams.get('priority');

    if (status) {
      setStatusFilter(status);
    }
    if (priority) {
      setPriorityFilter(priority);
    }
    // Note: 'overdue' filter will be handled in the filterDisputes function
  }, [searchParams])

  const [editForm, setEditForm] = useState({
    statusId: "",
    priorityId: "",
    assignedToUserId: "",
    dueDate: "",
    description: "",
    resolutionNotes: "",
    latestNote: "",
  })

  // Sync customer contacts when dispute is selected
  useEffect(() => {
    if (selectedDispute && selectedDisputeContacts.length > 0) {
      const newContacts = selectedDisputeContacts.map(c => ({
        id: c.id,
        name: `${c.firstName} ${c.surname}`,
        email: c.email,
        phone: c.phone
      }))
      setCustomerContacts(newContacts)
    }
  }, [selectedDispute?.id])

  // Initialize edited dispute when entering edit mode
  useEffect(() => {
    if (isEditMode && selectedDispute) {
      setEditedDispute({
        contactId: selectedDispute.contactId,
        contactName: selectedDispute.contactName,
        contactEmail: selectedDispute.contactEmail,
        contactPhone: selectedDispute.contactPhone,
        priority: selectedDispute.priority,
        assignedTo: selectedDispute.assignedTo,
      })
      // Initialize transactions
      setEditedTransactions(selectedDispute.transactions || [])
    } else {
      setEditedDispute({})
      setEditedTransactions([])
    }
  }, [isEditMode, selectedDispute])

  const handleCreateFormChange = (field: string, value: string) => {
    setCreateForm(prev => ({ ...prev, [field]: value }))
    
    // Auto-populate email and phone when contact is selected
    if (field === "contactId" && value) {
      const selectedContact = createFormContacts.find(c => c.id === value)
      if (selectedContact) {
        setCreateForm(prev => ({
          ...prev,
          contactEmail: selectedContact.email,
          contactPhone: selectedContact.phone
        }))
      }
    }
    
    // Reset contact fields when customer changes
    if (field === "customerId") {
      setCreateForm(prev => ({ ...prev, contactId: "", contactEmail: "", contactPhone: "" }))
      setCreateFormContacts([])
    }
  }
  
  // Sync contacts when customer is selected in create form
  useEffect(() => {
    if (createForm.customerId && createFormContactsData.length > 0) {
      const newContacts = createFormContactsData.map(c => ({
        id: c.id,
        name: `${c.firstName} ${c.surname}`,
        email: c.email,
        phone: c.phone
      }))
      setCreateFormContacts(newContacts)
    } else if (!createForm.customerId) {
      setCreateFormContacts([])
    }
  }, [createForm.customerId])

  const handleEditFormChange = (field: string, value: string) => {
    setEditForm(prev => ({ ...prev, [field]: value }))
  }

  const handleUpdateDispute = async () => {
    if (!selectedDispute) return

    const request: UpdateDisputeRequest = {}
    if (editForm.statusId) request.statusId = parseInt(editForm.statusId)
    if (editForm.priorityId) request.priorityId = parseInt(editForm.priorityId)
    if (editForm.assignedToUserId) request.assignedToUserId = parseInt(editForm.assignedToUserId)
    if (editForm.dueDate) request.dueDate = editForm.dueDate
    if (editForm.description) request.description = editForm.description
    if (editForm.resolutionNotes) request.resolutionNotes = editForm.resolutionNotes
    
    // If latestNote is provided, it will be added as a new note in the Activity tab
    // In a real implementation, this would be sent to the backend to create a new note
    if (editForm.latestNote) {
    }

    try {
      await updateDisputeMutation.mutateAsync(request)
      setIsViewDialogOpen(false)
      setEditForm({
        statusId: "",
        priorityId: "",
        assignedToUserId: "",
        dueDate: "",
        description: "",
        resolutionNotes: "",
        latestNote: "",
      })
      refetchDisputes()
    } catch (error) {
      console.error("Failed to update dispute:", error)
    }
  }

  const handleCreateDispute = async () => {
    if (!createForm.customerId || !createForm.disputeTypeId || !createForm.amount || !createForm.priorityId) {
      return
    }

    const request: CreateDisputeRequest = {
      customerId: parseInt(createForm.customerId),
      disputeTypeId: parseInt(createForm.disputeTypeId),
      amount: parseFloat(createForm.amount),
      statusId: parseInt(createForm.statusId),
      priorityId: parseInt(createForm.priorityId),
      assignedToUserId: createForm.assignedToUserId ? parseInt(createForm.assignedToUserId) : undefined,
      dueDate: createForm.dueDate || undefined,
      description: createForm.description || undefined,
      contactId: createForm.contactId ? parseInt(createForm.contactId) : undefined,
    }

    try {
      await createDisputeMutation.mutateAsync(request)
      setCreateForm(initialCreateFormState)
      setCreateFormContacts([])
      refetchDisputes()
    } catch (error) {
      console.error("Failed to create dispute:", error)
    }
  }

  // Map column IDs to backend sort field names
  const sortableColumnMap = useMemo<Record<string, string>>(() => ({
    disputeId: 'disputeId',
    customer: 'customer',
    type: 'type',
    amount: 'amount',
    status: 'status',
    priority: 'priority',
    assignedTo: 'assignedTo',
    dueDate: 'dueDate',
    createdDate: 'createdAt',
    workflow: 'workflow',
  }), []);

  const renderColumnHeader = (columnId: string) => {
    const headerMap: Record<string, string> = {
      disputeId: "Dispute ID",
      customer: "Customer",
      type: "Type",
      amount: "Amount",
      status: "Status",
      priority: "Priority",
      workflowProgress: "Workflow Progress",
      assignedTo: "Assigned To",
      dueDate: "Due Date",
      createdDate: "Created Date",
      workflow: "Workflow",
      description: "Description",
    }
    
    const label = headerMap[columnId] || columnId
    const backendField = sortableColumnMap[columnId];
    
    if (backendField) {
      return (
        <button
          onClick={() => handleSort(columnId)}
          className="flex items-center hover:text-foreground transition-colors"
        >
          {label}
          {getSortIcon(columnId)}
        </button>
      )
    }
    
    return label
  }

  const renderColumnCell = (columnId: string, dispute: DisputeUI) => {
    switch (columnId) {
      case "disputeId":
        return (
          <button
            onClick={() => handleViewDispute(dispute)}
            className="font-medium text-primary hover:underline"
          >
            {dispute.id}
          </button>
        )
      case "customer":
        return dispute.customer
      case "type":
        return dispute.type
      case "amount":
        return `£${dispute.amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` 
      case "status":
        return (
          <Badge className={statusColors[dispute.status as keyof typeof statusColors]}>
            {dispute.status}
          </Badge>
        )
      case "priority":
        return (
          <Badge className={priorityColors[dispute.priority as keyof typeof priorityColors]}>
            {dispute.priority}
          </Badge>
        )
      case "workflowProgress": {
        const totalLevels = dispute.totalLevels || getTotalLevelsForDisputeType(dispute.type);
        return (
          <div className="flex items-center gap-2">
            <div className="bg-gray-200 dark:bg-gray-700 rounded-full h-2 w-[100px] flex-shrink-0 overflow-hidden">
              <div
                className="bg-primary h-2 rounded-full"
                style={{ width: `${(dispute.currentLevel / totalLevels) * 100}%` }}
              />
            </div>
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {dispute.currentLevel}/{totalLevels}
            </span>
          </div>
        )
      }
      case "assignedTo":
        return dispute.assignedTo
      case "dueDate":
        return dispute.dueDate ? format(new Date(dispute.dueDate), "dd/MM/yyyy") : "—"
      case "createdDate":
        return format(new Date(dispute.createdDate), "MMM dd, yyyy")
      case "workflow":
        return dispute.workflow
      case "description":
        return (
          <span className="truncate max-w-[200px] block" title={dispute.description}>
            {dispute.description}
          </span>
        )
      default:
        return null
    }
  }

  // Apply overdue post-filter on already-paginated results
  const filteredDisputes = useMemo(() => {
    if (!overdueOnly) return disputes;
    return disputes.filter((x) => x.dueDate && isAfter(new Date(), parseISO(x.dueDate)));
  }, [disputes, overdueOnly]);

  // Derive the statuses of all selected disputes and determine quick action visibility
  const { selectedDisputeObjects, shouldHideAllActions, shouldShowReopenOnly } = useMemo(() => {
    const objs = selectedDisputeIds
      .map(id => filteredDisputes.find(d => d.id === id))
      .filter((d): d is DisputeUI => d !== undefined);
    const hasAnyResolved = objs.some(d => d.status === "Resolved");
    const allClosed = objs.length > 0 && objs.every(d => d.status === "Closed");
    const hasAnyClosed = objs.some(d => d.status === "Closed");
    const hasMixedWithClosed = hasAnyClosed && !allClosed;
    return {
      selectedDisputeObjects: objs,
      shouldHideAllActions: hasAnyResolved || hasMixedWithClosed,
      shouldShowReopenOnly: allClosed,
    };
  }, [selectedDisputeIds, filteredDisputes]);

  const renderActionsDropdown = () => {
    if (shouldShowReopenOnly) {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="secondary" size="sm" className="gap-2">
              Actions
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem onClick={() => setReopenDisputeOpen(true)} className="gap-2">
              <RotateCcw className="h-4 w-4" />
              Reopen
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }

    if (shouldHideAllActions) return null;

    const isSingle = selectedDisputeIds.length === 1;

    // ── Single dispute: primary inline buttons + More overflow dropdown ──
    if (isSingle) {
      return (
        <>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setReassignOpen(true)}
            className="gap-2"
          >
            <UserCog className="h-4 w-4" />
            Reassign User
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setUpdatePriorityOpen(true)}
            className="gap-2"
          >
            <Flag className="h-4 w-4" />
            Update Priority
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" size="sm" className="gap-2">
                More
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem onClick={() => setAddNoteOpen(true)} className="gap-2">
                <StickyNote className="h-4 w-4" />
                Add Note
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setSendEmailOpen(true)} className="gap-2">
                <Mail className="h-4 w-4" />
                Send Email
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setLogCallOpen(true)} className="gap-2">
                <Phone className="h-4 w-4" />
                Log Call
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setResolveDisputeOpen(true)} className="gap-2">
                <CheckCircle2 className="h-4 w-4" />
                Resolve Dispute
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setCloseDisputeOpen(true)} className="gap-2">
                <XCircle className="h-4 w-4" />
                Close Dispute
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      );
    }

    // ── Multiple disputes: inline action buttons ──────────────────────────
    return (
      <>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setReassignOpen(true)}
          className="gap-2"
        >
          <UserCog className="h-4 w-4" />
          Reassign User
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setUpdatePriorityOpen(true)}
          className="gap-2"
        >
          <Flag className="h-4 w-4" />
          Update Priority
        </Button>
      </>
    );
  };

  const renderReopenDialogDescription = () => {
    const statusText = selectedDisputeObjects.some(d => d.currentLevel !== 1)
      ? "its appropriate status based on workflow level"
      : "New status";
    if (selectedDisputeIds.length === 1) {
      return <>Are you sure you want to reopen dispute <span className="font-semibold">{selectedDisputeIds[0]}</span>? Each dispute will be set to {statusText}.</>;
    }
    return <>Are you sure you want to reopen <span className="font-semibold">{selectedDisputeIds.length} disputes</span>? Each dispute will be set to {statusText}.</>;
  };

  // Server-side sorting handlers
  const handleSort = useCallback((columnId: string) => {
    const backendField = sortableColumnMap[columnId];
    if (!backendField) return;

    if (sortBy === backendField) {
      if (sortOrder === 'asc') {
        setSortOrder('desc');
      } else {
        // Reset to default sort
        setSortBy('createdAt');
        setSortOrder('desc');
      }
    } else {
      setSortBy(backendField);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  }, [sortBy, sortOrder, sortableColumnMap]);

  const getSortIcon = useCallback((columnId: string) => {
    const backendField = sortableColumnMap[columnId];
    if (!backendField) return null;

    if (sortBy !== backendField) {
      return <ArrowUpDown className="ml-2 h-4 w-4" />
    }
    if (sortOrder === 'asc') {
      return <ArrowUp className="ml-2 h-4 w-4" />
    }
    return <ArrowDown className="ml-2 h-4 w-4" />
  }, [sortBy, sortOrder, sortableColumnMap]);

  // Server-side pagination values
  const totalRecords = pagination.totalItems
  const totalPages = pagination.totalPages

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [statusFilter, priorityFilter])

  // Reset to last page if current page exceeds total pages
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages)
    }
  }, [totalPages, currentPage])

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page)
    }
  }

  const handlePageSizeChange = (newSize: string) => {
    const size = parseInt(newSize)
    setPageSize(size)
    setCurrentPage(1)
  }

  const pageNumbers = getPageNumbers(currentPage, totalPages)

  const handleViewDispute = (dispute: DisputeUI) => {
    // Navigate to full-screen dispute details page
    navigate(`/disputes/all/${dispute.id}`, {
      state: { from: "all" }
    });
  }

  const handleExportToExcel = async () => {
    if (filteredDisputes.length === 0) return
    setIsExporting(true)
    try {
      exportDisputesToExcel(
        filteredDisputes,
        `disputes-${new Date().toISOString().split("T")[0]}.csv`
      )
    } catch (error) {
      console.error("Error exporting disputes:", error)
    } finally {
      setIsExporting(false)
    }
  }

  const handleExportAllColumns = async () => {
    if (filteredDisputes.length === 0) return
    setIsExporting(true)
    try {
      exportDisputesToExcel(
        filteredDisputes,
        `disputes-all-columns-${new Date().toISOString().split("T")[0]}.csv`
      )
    } catch (error) {
      console.error("Error exporting disputes:", error)
    } finally {
      setIsExporting(false)
    }
  }

  const handleNextAction = () => {
    setIsNextActionModalOpen(true)
  }

  const handleProceedToNextStage = () => {
    if (!nextActionNotes.trim()) {
      toast.error('Notes are required')
      return
    }

    if (!selectedDispute) {
      toast.error('No dispute selected')
      return
    }

    // Get the next stage information
    const workflowConfig = getWorkflowForDisputeType(selectedDispute.type);
    const nextStage = workflowConfig.stages.find(s => s.level === selectedDispute.currentLevel + 1);
    
    if (nextStage) {
      // Log workflow progress activity
      const newActivity: CollectionActivity = {
        id: `act-${Date.now()}`,
        caseId: selectedDispute.id,
        type: "workflow_progress",
        description: `Advanced to workflow stage: ${nextStage.title}`,
        outcome: nextActionNotes,
        createdBy: "Current User",
        createdAt: new Date(),
      };
      setActivities([newActivity, ...activities]);
      
      toast.success(`Successfully moved to: ${nextStage.title}`);
    }
    
    // Close modal and reset
    setIsNextActionModalOpen(false)
    setNextActionNotes('')
    setNextActionFollowUpDate('')
  }

  const handleCancelNextAction = () => {
    setIsNextActionModalOpen(false)
    setNextActionNotes('')
    setNextActionFollowUpDate('')
  }

  // ── Quick action handlers ──────────────────────────────────────────────────

  const handleQuickAddNote = async (content: string) => {
    if (selectedDisputeNumericId) {
      try {
        await addDisputeNoteMutation.mutateAsync({
          disputeId: selectedDisputeNumericId.toString(),
          content,
          userId: "0",
          userName: "Current User",
        })
        toast.success("Note added successfully")
      } catch {
        toast.error("Failed to add note")
        throw new Error("Failed to add note")
      }
    } else {
      const newNote: CaseNote = {
        id: `note-${Date.now()}`,
        caseId: selectedDispute?.id || "",
        content,
        createdBy: "Current User",
        createdAt: new Date(),
      }
      setNotes([newNote, ...notes])
      toast.success("Note added successfully")
    }
  }

  const handleQuickReassign = async () => {
    if (!reassignUserId) return
    const user = users.find(u => u.id.toString() === reassignUserId)
    const userName = user ? `${user.firstName} ${user.lastName}` : reassignUserId
    const count = selectedDisputeIds.length
    await quickUpdateDisputes.mutateAsync(
      selectedDisputeIds.map(id => ({ disputeStringId: id, numericId: filteredDisputes.find(d => d.id === id)?.numericId, assignedTo: userName, assignedToUserId: parseInt(reassignUserId, 10) }))
    )
    toast.success(`${count} dispute${count > 1 ? "s" : ""} reassigned to ${userName}`)
    setReassignUserId("")
    setReassignOpen(false)
    refetchDisputes()
  }

  const handleQuickUpdatePriority = async () => {
    if (!updatePriorityValue) return
    const count = selectedDisputeIds.length
    const matchedPriority = disputePriorities.find(p => p.name === updatePriorityValue)
    await quickUpdateDisputes.mutateAsync(
      selectedDisputeIds.map(id => ({ disputeStringId: id, numericId: filteredDisputes.find(d => d.id === id)?.numericId, priority: updatePriorityValue, priorityId: matchedPriority?.id }))
    )
    toast.success(`Priority updated to ${updatePriorityValue} for ${count} dispute${count > 1 ? "s" : ""}`)
    setUpdatePriorityValue("")
    setUpdatePriorityOpen(false)
    refetchDisputes()
  }

  const handleQuickCloseDispute = async () => {
    if (closeReason === 'other' && !closeNote.trim()) return
    const disputeId = selectedDispute?.id
    if (!disputeId) return
    const closeReasonLabels = {
      duplicate: 'Duplicate Entry',
      customer_withdrew: 'Customer Withdrew Dispute',
      invalid_dispute: 'Invalid Dispute',
      other: 'Other',
    }
    const composedCloseNote = closeNote
      ? `Dispute closed - Reason: ${closeReasonLabels[closeReason]}\n\nNote: ${closeNote}`
      : `Dispute closed - Reason: ${closeReasonLabels[closeReason]}`;
    await quickUpdateDisputes.mutateAsync([{
      disputeStringId: disputeId,
      numericId: selectedDispute?.numericId,
      status: "Closed",
      closedReason: closeReason,
      closedDate: new Date().toISOString().split("T")[0],
      closedNote: composedCloseNote,
    }])
    if (selectedDisputeNumericId) {
      await addDisputeNoteMutation.mutateAsync({
        disputeId: selectedDisputeNumericId.toString(),
        content: composedCloseNote,
        userId: "0",
        userName: "Current User",
      })
    }
    toast.success(`Dispute ${disputeId} closed`)
    setCloseReason('duplicate')
    setCloseNote('')
    setCloseDisputeOpen(false)
    setSelectedDisputeIds([])
    setSelectedDispute(null)
    refetchDisputes()
  }

  const handleQuickResolveDispute = useCallback(async (outcome: 'upheld' | 'denied' | 'partial', amount: string, notes: string) => {
    const disputeId = selectedDispute?.id
    if (!disputeId) return
    await quickUpdateDisputes.mutateAsync([{
      disputeStringId: disputeId,
      numericId: selectedDispute?.numericId,
      status: "Resolved",
      resolutionNotes: notes,
      resolutionOutcome: outcome,
      resolutionAmount: outcome === 'partial' ? parseFloat(amount) : selectedDispute?.amount ?? null,
      resolutionDate: new Date().toISOString().split("T")[0],
      resolvedBy: "Current User",
    }])
    toast.success(`Dispute ${disputeId} marked as resolved`)
    setResolveDisputeOpen(false)
    setSelectedDisputeIds([])
    setSelectedDispute(null)
    refetchDisputes()
  }, [selectedDispute, quickUpdateDisputes, refetchDisputes])

  const handleReopenDispute = async () => {
    const updates = selectedDisputeObjects.map(d => ({
      disputeStringId: d.id,
      numericId: d.numericId,
      status: d.currentLevel === 1 ? "New" : "In Progress",
    }));
    await quickUpdateDisputes.mutateAsync(updates);
    const count = updates.length;
    toast.success(`${count} dispute${count > 1 ? "s" : ""} reopened`);
    setReopenDisputeOpen(false);
    setSelectedDisputeIds([]);
    setSelectedDispute(null);
    refetchDisputes();
  };

  // Toggle a single row
  const handleToggleRow = (dispute: DisputeUI) => {
    setSelectedDisputeIds(prev => {
      if (prev.includes(dispute.id)) {
        const next = prev.filter(id => id !== dispute.id)
        setSelectedDispute(next.length === 1
          ? filteredDisputes.find(d => d.id === next[0]) ?? null
          : null)
        return next
      } else {
        const next = [...prev, dispute.id]
        setSelectedDispute(next.length === 1 ? dispute : null)
        return next
      }
    })
  }

  // Select / deselect all on current page
  const allPageSelected = filteredDisputes.length > 0 &&
    filteredDisputes.every(d => selectedDisputeIds.includes(d.id))
  const somePageSelected = filteredDisputes.some(d => selectedDisputeIds.includes(d.id)) && !allPageSelected

  const handleToggleAll = () => {
    if (allPageSelected) {
      setSelectedDisputeIds(prev => prev.filter(id => !filteredDisputes.some(d => d.id === id)))
      setSelectedDispute(null)
    } else {
      const pageIds = filteredDisputes.map(d => d.id)
      setSelectedDisputeIds(prev => Array.from(new Set([...prev, ...pageIds])))
      setSelectedDispute(null)
    }
  }

  const handleSaveEditedDispute = async () => {
    if (!selectedDispute) return

    try {
      // Here you would call the update API with the edited fields
      // For now, we'll just close edit mode and update local state
      
      // Update the selected dispute with edited values including transactions
      const updatedDispute = {
        ...selectedDispute,
        ...editedDispute,
        transactions: editedTransactions,
      }
      
      setSelectedDispute(updatedDispute)
      setIsEditMode(false)
      
      refetchDisputes()
    } catch (error) {
      console.error("Failed to save dispute:", error)
    }
  }

  const updateTransactionField = (index: number, field: string, value: string | number) => {
    const updated = [...editedTransactions]
    ;(updated[index] as Record<string, string | number>)[field] = value
    setEditedTransactions(updated)
  }

  const removeTransaction = (index: number) => {
    setEditedTransactions(editedTransactions.filter((_, i) => i !== index))
  }

  const renderNextActionPanel = () => {
    if (!selectedDispute) return null;
    const workflowConfig = getWorkflowForDisputeType(selectedDispute.type);
    const currentStage = workflowConfig.stages.find(s => s.level === selectedDispute.currentLevel);
    const nextStage = workflowConfig.stages.find(s => s.level === selectedDispute.currentLevel + 1);
    
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Zap className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            Next Action
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {currentStage && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold">{currentStage.title}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Current Stage (Level {currentStage.level} of {workflowConfig.stages.length})
                  </p>
                </div>
                <Badge variant="outline" className="bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800">
                  In Progress
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                {currentStage.description}
              </p>
            </div>
          )}
          
          {nextStage && (
            <div className="pt-4 border-t space-y-3">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                  Next Stage
                </p>
                <p className="text-sm font-semibold">{nextStage.title}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {nextStage.description}
                </p>
                {Boolean(nextStage.estimatedDays) && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Estimated: {nextStage.estimatedDays} day{nextStage.estimatedDays !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
              <Button 
                className="w-full gap-2"
                onClick={handleNextAction}
              >
                <CheckCircle2 className="h-4 w-4" />
                Complete {currentStage?.title || 'Current Stage'} & Advance
              </Button>
            </div>
          )}
          
          {!nextStage && selectedDispute.currentLevel === workflowConfig.stages.length && (
            <div className="pt-4 border-t">
              <div className="flex items-center gap-2 text-green-600 dark:text-green-400 mb-2">
                <CheckCircle2 className="h-4 w-4" />
                <p className="text-sm font-semibold">Final Stage Reached</p>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                All workflow stages have been completed. Ready to close dispute.
              </p>
              <Button 
                className="w-full gap-2 bg-green-600 hover:bg-green-700"
                onClick={() => {
                  // TODO: implement close dispute from final stage
                }}
              >
                <CheckCircle2 className="h-4 w-4" />
                Close Dispute
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const getTransactionList = () => {
    if (!selectedDispute) return [];
    if (isEditMode) return editedTransactions;
    return selectedDispute.transactions || [];
  };
  const transactionList = getTransactionList();

  const renderActiveFilters = () => {
    if (!overdueOnly && statusFilter === "all" && priorityFilter === "all" && assignedUserFilter === "all") return null;
    return (
      <div className="flex items-center gap-2 mt-2">
        <span className="text-xs text-muted-foreground">Active filters:</span>
        {overdueOnly && (
          <Badge variant="secondary" className="text-xs">
            <AlertTriangle className="h-3 w-3 mr-1" />
            Overdue Only
          </Badge>
        )}
        {statusFilter !== "all" && (
          <Badge variant="secondary" className="text-xs">
            Status: {statusFilter}
          </Badge>
        )}
        {priorityFilter !== "all" && (
          <Badge variant="secondary" className="text-xs">
            Priority: {priorityFilter}
          </Badge>
        )}
        {assignedUserFilter !== "all" && (
          <Badge variant="secondary" className="text-xs">
            User: {userFilterOptions.find(u => String(u.id) === assignedUserFilter)?.displayName ?? assignedUserFilter}
          </Badge>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="h-6 text-xs"
          onClick={() => {
            setStatusFilter("all");
            setPriorityFilter("all");
            setAssignedUserFilter("all");
            setSearchParams({});
          }}
        >
          <X className="h-3 w-3 mr-1" />
          Clear all
        </Button>
      </div>
    );
  };

  const renderTableBody = () => {
    if (isLoading) {
      return (
        <TableRow>
          <TableCell colSpan={visibleColumns.length + 1} className="h-64 text-center">
            <div className="flex flex-col items-center justify-center space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Loading disputes...</p>
            </div>
          </TableCell>
        </TableRow>
      );
    }
    if (filteredDisputes.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={visibleColumns.length + 1} className="h-64 text-center">
            <p className="text-sm text-muted-foreground">No disputes found</p>
          </TableCell>
        </TableRow>
      );
    }
    return filteredDisputes.map((dispute) => (
      <TableRow
        key={dispute.id}
        className={selectedDisputeIds.includes(dispute.id) ? "bg-muted/50" : ""}
      >
        <TableCell>
          <Checkbox
            checked={selectedDisputeIds.includes(dispute.id)}
            onCheckedChange={() => handleToggleRow(dispute)}
            aria-label={`Select dispute ${dispute.id}`}
          />
        </TableCell>
        {visibleColumns.map((col) => (
          <TableCell key={col.id}>{renderColumnCell(col.id, dispute)}</TableCell>
        ))}
      </TableRow>
    ));
  };

  const renderPagination = () => {
    if (isLoading || filteredDisputes.length === 0) return null;
    return (
      <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
          <div className="text-sm text-muted-foreground">
            Showing {Math.min((currentPage - 1) * pageSize + 1, totalRecords)} to {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords} disputes
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="page-size" className="text-sm text-muted-foreground whitespace-nowrap">
              Rows per page:
            </Label>
            <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
              <SelectTrigger id="page-size" className="h-8 w-[70px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {totalPages > 1 && (
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => handlePageChange(currentPage - 1)}
                  className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                />
              </PaginationItem>
              {pageNumbers.map((page, index) => (
                <PaginationItem key={`page-${page}-${index}`}>
                  {typeof page === 'string' ? (
                    <PaginationEllipsis />
                  ) : (
                    <PaginationLink
                      onClick={() => handlePageChange(page as number)}
                      isActive={currentPage === page}
                      className="cursor-pointer"
                    >
                      {page}
                    </PaginationLink>
                  )}
                </PaginationItem>
              ))}
              <PaginationItem>
                <PaginationNext
                  onClick={() => handlePageChange(currentPage + 1)}
                  className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    );
  };

  const renderReopenDialog = () => (
    <Dialog open={reopenDisputeOpen} onOpenChange={setReopenDisputeOpen}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5 text-primary" />
            Reopen Dispute{selectedDisputeIds.length > 1 ? "s" : ""}
          </DialogTitle>
          <DialogDescription>
            {renderReopenDialogDescription()}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setReopenDisputeOpen(false)}>Cancel</Button>
          <Button onClick={handleReopenDispute} className="gap-2">
            <RotateCcw className="h-4 w-4" />
            Confirm Reopen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const totalDisputes = totals.totalCount
  const activeDisputes = totals.activeCount
  const totalAmount = totals.totalAmount
  const previousMonthCount = totals.previousMonthCount
  const newThisWeekCount = totals.newThisWeekCount

  const monthChangePercent = previousMonthCount > 0
    ? Math.round(((totalDisputes - previousMonthCount) / previousMonthCount) * 100)
    : 0;
  const monthChangeIsPositive = monthChangePercent > 0;
  const monthChangeLabel = monthChangeIsPositive ? `+${monthChangePercent}%` : `${monthChangePercent}%`;

  return (
    <div className="space-y-6 p-6" style={{ minHeight: '100vh', backgroundColor: 'var(--background)' }}>
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold tracking-tight">Dispute Management</h1>
            <Badge variant="outline" className="ml-2">
              {totalRecords} disputes
            </Badge>
          </div>
          <p className="text-muted-foreground">
            Track, manage, and resolve customer disputes
          </p>
        </div>
        <div className="flex items-center gap-2">
          <FeatureFeedbackCollector feature="Disputes" />
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {/* Total Disputes */}
        <Card className="border-l-4 border-l-blue-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Disputes</CardTitle>
            <Activity className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalDisputes}</div>
            <div className="flex items-center text-xs text-muted-foreground mt-1">
              <TrendingDown className={`mr-1 h-3 w-3 ${monthChangeIsPositive ? "text-red-600" : "text-green-600"}`} />
              <span className={`${monthChangeIsPositive ? "text-red-600" : "text-green-600"} font-medium`}>{monthChangeLabel}</span>
              <span className="ml-1">from last month</span>
            </div>
          </CardContent>
        </Card>

        {/* Active Disputes */}
        <Card className="border-l-4 border-l-amber-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Disputes</CardTitle>
            <Clock className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeDisputes}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {newThisWeekCount} new this week
            </p>
          </CardContent>
        </Card>

        {/* Disputed Amount */}
        <Card className="border-l-4 border-l-emerald-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Disputed Amount</CardTitle>
            <PoundSterling className="h-4 w-4 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">£{totalAmount.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Across all active disputes
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>All Disputes</CardTitle>
              <CardDescription>
                Comprehensive view of all customer disputes
              </CardDescription>
              {/* Active Filters Indicator */}
              {renderActiveFilters()}
            </div>
            <div className="flex items-center gap-2">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search disputes..."
                  className="pl-8 w-[250px]"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]">
                  <Filter className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="New">New</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Resolved">Resolved</SelectItem>
                  <SelectItem value="Closed">Closed</SelectItem>
                </SelectContent>
              </Select>

              {/* Priority Filter */}
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="w-[150px]">
                  <Filter className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priority</SelectItem>
                  {disputePriorities.map((p) => (
                    <SelectItem key={p.code} value={p.name}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* User Filter */}
              <Select value={assignedUserFilter} onValueChange={setAssignedUserFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Assigned User" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {userFilterOptions.map((user) => (
                    <SelectItem key={user.id} value={String(user.id)}>
                      {user.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Export to Excel */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={isExporting || filteredDisputes.length === 0}
                    title="Export"
                  >
                    <FileSpreadsheet className={cn("h-4 w-4", isExporting && "animate-pulse")} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleExportToExcel}>
                    Export Current View
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportAllColumns}>
                    Export All Columns
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Column Configuration */}
              <Button
                variant="outline"
                size="icon"
                onClick={() => setIsColumnConfigOpen(true)}
                title="Configure columns"
              >
                <Settings className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">
                  <Checkbox
                    checked={allPageSelected}
                    ref={(el: HTMLButtonElement | null) => { if (el) el.dataset.indeterminate = somePageSelected ? "true" : "false" }}
                    onCheckedChange={handleToggleAll}
                    aria-label="Select all disputes on this page"
                    className={somePageSelected ? "opacity-70" : ""}
                  />
                </TableHead>
                {visibleColumns.map((col) => (
                  <TableHead key={col.id}>{renderColumnHeader(col.id)}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {renderTableBody()}
            </TableBody>
          </Table>

          {/* Pagination Controls */}
          {renderPagination()}
        </CardContent>
      </Card>

      {/* Column Configuration Dialog */}
      <ColumnConfigDialog
        open={isColumnConfigOpen}
        onOpenChange={setIsColumnConfigOpen}
        columns={disputeColumns}
        onApply={saveDisputeColumnPreferences}
        onReset={resetDisputeColumnPreferences}
      />

      {/* View Dispute Sheet */}
      <Sheet open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto px-6">
          {selectedDispute && (
            <>
              <SheetHeader className="px-0">
                <div className="flex items-start justify-between">
                  <div className="space-y-1.5">
                    <SheetTitle>Dispute Details - {selectedDispute.id}</SheetTitle>
                    <SheetDescription>
                      View and manage dispute information
                    </SheetDescription>
                  </div>
                  <div className="flex gap-2">
                    {isEditMode && (
                      <Button
                        size="sm"
                        onClick={handleSaveEditedDispute}
                        className="gap-2"
                      >
                        <CheckCircle2 className="h-4 w-4" />
                        Save Changes
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsEditMode(!isEditMode)}
                      className="gap-2"
                    >
                      <Pencil className="h-4 w-4" />
                      {isEditMode ? "Cancel" : "Edit Dispute"}
                    </Button>
                  </div>
                </div>
              </SheetHeader>

              <Tabs defaultValue="overview" className="mt-6">
                <TabsList className="grid w-full grid-cols-6">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="transactions">Transactions</TabsTrigger>
                  <TabsTrigger value="workflow">Workflow</TabsTrigger>
                  <TabsTrigger value="activity">Activity</TabsTrigger>
                  <TabsTrigger value="documents">Documents</TabsTrigger>
                </TabsList>

                {/* Tab 1: Overview */}
                <TabsContent value="overview" className="space-y-3">
                  {/* Top Row: Customer Information and Dispute Summary */}
                  <div className="grid gap-3 md:grid-cols-2">
                    {/* Customer Information Panel */}
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                          <User className="h-4 w-4 text-primary" />
                          Customer Information
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            Customer
                          </p>
                          <p className="text-sm font-semibold">
                            {selectedDispute.customerAccountCode} - {selectedDispute.customer}
                          </p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                            <User className="h-3 w-3" />
                            Contact Name
                          </p>
                          {isEditMode ? (
                            <Select
                              value={String(editedDispute.contactId ?? selectedDispute.contactId ?? "")}
                              onValueChange={(value) => {
                                const contact = customerContacts.find(c => String(c.id) === value)
                                setEditedDispute(prev => ({
                                  ...prev,
                                  contactId: contact ? Number(contact.id) : prev.contactId,
                                  contactName: contact?.name || prev.contactName,
                                  contactEmail: contact?.email || prev.contactEmail,
                                  contactPhone: contact?.phone || prev.contactPhone,
                                }))
                              }}
                            >
                              <SelectTrigger className="h-8">
                                <SelectValue placeholder={editedDispute.contactName || selectedDispute.contactName || "Select contact"} />
                              </SelectTrigger>
                              <SelectContent>
                                {customerContacts.map((contact) => (
                                  <SelectItem key={String(contact.id)} value={String(contact.id)}>
                                    {contact.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <p className="text-sm font-semibold">{selectedDispute.contactName}</p>
                          )}
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            Email
                          </p>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              onClick={() => setSendEmailOpen(true)}
                              className="gap-2 bg-teal-600 hover:bg-teal-700 text-white h-7 px-2 shrink-0"
                            >
                              <Mail className="h-3.5 w-3.5" />
                            </Button>
                            <p className="text-sm font-semibold break-all">{isEditMode ? (editedDispute.contactEmail ?? selectedDispute.contactEmail) : selectedDispute.contactEmail}</p>
                          </div>
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            Phone
                          </p>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              onClick={() => setLogCallOpen(true)}
                              className="gap-2 bg-teal-600 hover:bg-teal-700 text-white h-7 px-2 shrink-0"
                            >
                              <Phone className="h-3.5 w-3.5" />
                            </Button>
                            <p className="text-sm font-semibold">{isEditMode ? (editedDispute.contactPhone ?? selectedDispute.contactPhone) : selectedDispute.contactPhone}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Dispute Summary Panel */}
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                          Dispute Summary
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                              <FileText className="h-3 w-3" />
                              Dispute ID
                            </p>
                            <p className="text-sm font-semibold">{selectedDispute.id}</p>
                          </div>
                          <div className="space-y-1">
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                              <DollarSign className="h-3 w-3" />
                              Amount
                            </p>
                            <p className="text-base font-bold text-red-600 dark:text-red-400">
                              £{selectedDispute.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                          </div>
                          <div className="space-y-1">
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Dispute Type</p>
                            <p className="text-sm font-semibold">{selectedDispute.type}</p>
                          </div>
                          <div className="space-y-1.5">
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Status</p>
                            <Badge className={cn(statusColors[selectedDispute.status as keyof typeof statusColors] || "bg-gray-500", "w-fit")}>
                              {selectedDispute.status}
                            </Badge>
                          </div>
                          <div className="space-y-1.5">
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Priority</p>
                            {isEditMode ? (
                              <Select
                                value={editedDispute.priority || selectedDispute.priority}
                                onValueChange={(value) => setEditedDispute(prev => ({ ...prev, priority: value }))}
                              >
                                <SelectTrigger className="h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {disputePriorities.map((p) => (
                                    <SelectItem key={p.code} value={p.name}>{p.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <Badge className={cn(priorityColors[selectedDispute.priority as keyof typeof priorityColors] || "bg-gray-500", "w-fit")}>
                                {selectedDispute.priority}
                              </Badge>
                            )}
                          </div>
                          <div className="space-y-1">
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                              <UserCheck className="h-3 w-3" />
                              Assigned To
                            </p>
                            {isEditMode ? (
                              <Select
                                value={editedDispute.assignedTo || selectedDispute.assignedTo}
                                onValueChange={(value) => setEditedDispute(prev => ({ ...prev, assignedTo: value }))}
                              >
                                <SelectTrigger className="h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {users.map((user) => (
                                    <SelectItem key={user.id} value={`${user.firstName} ${user.lastName}`}>
                                      {`${user.firstName} ${user.lastName}`}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <p className="text-sm font-semibold">{selectedDispute.assignedTo}</p>
                            )}
                          </div>
                          <div className="space-y-1.5 col-span-2">
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                              <GitBranch className="h-3 w-3" />
                              Workflow Level
                            </p>
                            <div className="space-y-1.5">
                              <div className="flex items-center justify-between text-xs">
                                <span className="font-semibold">{selectedDispute.workflow}</span>
                                <span className="text-muted-foreground">
                                  Level {selectedDispute.currentLevel} of {selectedDispute.totalLevels || getTotalLevelsForDisputeType(selectedDispute.type)}
                                </span>
                              </div>
                              <Progress 
                                value={(selectedDispute.currentLevel / (selectedDispute.totalLevels || getTotalLevelsForDisputeType(selectedDispute.type))) * 100} 
                                className="h-2" 
                              />
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {renderNextActionPanel()}

                  {/* Dispute Notes Panel */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <MessageSquare className="h-4 w-4 text-green-600 dark:text-green-400" />
                        Dispute Notes
                      </CardTitle>
                      <CardDescription>
                        {isEditMode ? "Edit the latest note or add a new one" : "Latest note from activity timeline"}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {mockNotes.length > 0 && (
                        <div className="space-y-2">
                          {isEditMode ? (
                            <div className="space-y-2">
                              <Textarea 
                                placeholder="Add or edit dispute note..." 
                                rows={4}
                                className="text-sm"
                                value={editForm.latestNote || mockNotes[0].content}
                                onChange={(e) => handleEditFormChange("latestNote", e.target.value)}
                              />
                              <p className="text-xs text-muted-foreground">
                                Saving will update the Activity tab with a new note entry
                              </p>
                            </div>
                          ) : (
                            <>
                              <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
                                {mockNotes[0].content}
                              </p>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground pt-2 border-t">
                                <User className="h-3 w-3" />
                                <span>{mockNotes[0].createdBy}</span>
                                <span>•</span>
                                <Clock className="h-3 w-3" />
                                <span>{format(mockNotes[0].createdAt, "MMM dd, yyyy 'at' h:mm a")}</span>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                      {mockNotes.length === 0 && (
                        <div className="text-center py-6">
                          <MessageSquare className="h-8 w-8 text-muted-foreground mx-auto mb-2 opacity-50" />
                          <p className="text-sm text-muted-foreground">No notes yet</p>
                          {isEditMode && (
                            <p className="text-xs text-muted-foreground mt-1">Add the first note above</p>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Tab 2: Transactions */}
                <TabsContent value="transactions" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-base flex items-center gap-2">
                            <Receipt className="h-4 w-4 text-primary" />
                            Transactions Under Dispute
                          </CardTitle>
                          <CardDescription>
                            {isEditMode ? 'Add, edit, or remove transactions' : 'View transactions associated with this dispute'}
                          </CardDescription>
                        </div>
                        {isEditMode && (
                          <Button
                            size="sm"
                            onClick={() => {
                              const newTransaction = {
                                id: `temp-${Date.now()}`,
                                referenceNumber: '',
                                date: new Date().toISOString().split('T')[0],
                                dueDate: '',
                                description: '',
                                transactionType: 'invoice' as const,
                                amount: 0,
                                currency: 'GBP',
                                status: 'outstanding' as const,
                              }
                              setEditedTransactions([...editedTransactions, newTransaction])
                            }}
                            className="gap-2"
                          >
                            <Plus className="h-4 w-4" />
                            Add Transaction
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      {transactionList.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                          <Receipt className="h-12 w-12 mx-auto mb-3 opacity-50" />
                          <p className="font-medium">No transactions found</p>
                          {isEditMode && (
                            <p className="text-sm mt-2">Click &quot;Add Transaction&quot; to add a transaction to this dispute</p>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Transaction ID</TableHead>
                                <TableHead>Date</TableHead>
                                <TableHead>Description</TableHead>
                                <TableHead className="text-right">Amount</TableHead>
                                {isEditMode && <TableHead className="w-[50px]"></TableHead>}
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {transactionList.map((transaction, index) => (
                                <TableRow key={transaction.id}>
                                  <TableCell>
                                    {isEditMode ? (
                                      <Input
                                        value={transaction.referenceNumber}
                                        onChange={(e) => updateTransactionField(index, 'referenceNumber', e.target.value)}
                                        placeholder="TXN-001"
                                        className="h-8"
                                      />
                                    ) : (
                                      <span className="font-mono text-sm">{transaction.referenceNumber}</span>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {isEditMode ? (
                                      <Input
                                        type="date"
                                        value={transaction.date}
                                        onChange={(e) => updateTransactionField(index, 'date', e.target.value)}
                                        className="h-8"
                                      />
                                    ) : (
                                      format(new Date(transaction.date), 'dd MMM yyyy')
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {isEditMode ? (
                                      <Input
                                        value={transaction.description}
                                        onChange={(e) => updateTransactionField(index, 'description', e.target.value)}
                                        placeholder="Transaction description"
                                        className="h-8"
                                      />
                                    ) : (
                                      transaction.description
                                    )}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {isEditMode ? (
                                      <Input
                                        type="number"
                                        step="0.01"
                                        value={transaction.amount}
                                        onChange={(e) => updateTransactionField(index, 'amount', parseFloat(e.target.value) || 0)}
                                        className="h-8 text-right"
                                      />
                                    ) : (
                                      <span className="font-semibold">
                                        £{transaction.amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                      </span>
                                    )}
                                  </TableCell>
                                  {isEditMode && (
                                    <TableCell>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => removeTransaction(index)}
                                        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </TableCell>
                                  )}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                          
                          <div className="flex justify-end pt-4 border-t">
                            <div className="text-right space-y-1">
                              <p className="text-sm text-muted-foreground">Total Disputed Amount</p>
                              <p className="text-2xl font-bold text-primary">
                                  £{transactionList
                                    .reduce((sum, t) => sum + t.amount, 0)
                                    .toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Tab 3: Workflow */}
                <TabsContent value="workflow" className="space-y-4">
                  {(() => {
                    // Get the workflow configuration for this dispute type
                    const workflowConfig = getWorkflowForDisputeType(selectedDispute.type);
                    const workflowStages = workflowConfig.stages;
                    
                    // Generate completion dates based on current level
                    // First stage completed on created date, subsequent stages 1 day apart
                    const baseDate = new Date(selectedDispute.createdDate);
                    const stagesWithDates = workflowStages.map((stage, index) => ({
                      ...stage,
                      completedDate: stage.level <= selectedDispute.currentLevel 
                        ? new Date(baseDate.getTime() + index * 24 * 60 * 60 * 1000).toISOString()
                        : null,
                    }));

                    return (
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg">Active Workflow</CardTitle>
                          <CardDescription>{workflowConfig.workflowName}</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="flex items-center gap-2 mb-6">
                            <span className="text-sm font-medium">Current Level:</span>
                            <Badge>{selectedDispute.currentLevel} of {workflowStages.length}</Badge>
                          </div>

                          <div className="space-y-6">
                            {stagesWithDates.map((step, index) => (
                              <div key={step.level} className="flex gap-4">
                                <div className="flex flex-col items-center">
                                  <div className={`flex h-8 w-8 items-center justify-center rounded-full ${
                                    step.level <= selectedDispute.currentLevel
                                      ? "bg-primary text-primary-foreground"
                                      : "bg-muted text-muted-foreground"
                                  }`}>
                                    {step.level < selectedDispute.currentLevel ? (
                                      <CheckCircle2 className="h-4 w-4" />
                                    ) : step.level === selectedDispute.currentLevel ? (
                                      <Clock className="h-4 w-4" />
                                    ) : (
                                      <span className="text-sm">{step.level}</span>
                                    )}
                                  </div>
                                  {index < workflowStages.length - 1 && (
                                    <div className={`w-0.5 h-12 ${
                                      step.level < selectedDispute.currentLevel
                                        ? "bg-primary"
                                        : "bg-muted"
                                    }`} />
                                  )}
                                </div>
                                <div className="flex-1 pb-8">
                                  <h4 className="font-semibold">{step.title}</h4>
                                  <p className="text-sm text-muted-foreground mt-1">
                                    {step.description}
                                  </p>
                                  {step.completedDate && (
                                    <p className="text-xs text-muted-foreground mt-2">
                                      Completed: {format(new Date(step.completedDate), "MMM dd, yyyy")}
                                    </p>
                                  )}
                                  {Boolean(step.estimatedDays) && !step.completedDate && (
                                    <p className="text-xs text-muted-foreground mt-2">
                                      Estimated: {step.estimatedDays} day{step.estimatedDays !== 1 ? 's' : ''}
                                    </p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })()}
                </TabsContent>

                {/* Tab 4: Activity */}
                <TabsContent value="activity" className="space-y-4">
                  <ConsolidatedActivityTimeline
                    activities={activities.filter(a => a.caseId === selectedDispute?.id)}
                    communications={communications.filter(c => c.caseId === selectedDispute?.id)}
                    notes={notes.filter(n => n.caseId === selectedDispute?.id)}
                    isEditMode={isEditMode}
                    onAddNote={async (content: string) => {
                      // Add note to the timeline
                      const newNote: CaseNote = {
                        id: `note-${Date.now()}`,
                        caseId: selectedDispute?.id || "",
                        content: content,
                        createdBy: "Current User",
                        createdAt: new Date(),
                      };
                      setNotes([newNote, ...notes]);
                      toast.success("Note added to timeline");
                    }}
                  />
                </TabsContent>

                {/* Tab 5: Documents */}
                <TabsContent value="documents" className="space-y-4">
                  <Button 
                    className="w-full"
                    onClick={() => {
                      // Log document upload activity
                      const newActivity: CollectionActivity = {
                        id: `act-${Date.now()}`,
                        caseId: selectedDispute?.id || "",
                        type: "document_upload",
                        description: "Document uploaded to dispute",
                        outcome: "Document added successfully",
                        createdBy: "Current User",
                        createdAt: new Date(),
                      };
                      setActivities([newActivity, ...activities]);
                      toast.success("Document upload logged to timeline");
                    }}
                  >
                    <FileText className="mr-2 h-4 w-4" />
                    Upload Document
                  </Button>
                  <div className="space-y-3">
                    {documents.map((doc, index) => (
                      <Card key={index}>
                        <CardContent className="pt-6">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <FileText className="h-8 w-8 text-muted-foreground" />
                              <div>
                                <p className="font-medium text-sm">{doc.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {doc.size} • Uploaded {format(new Date(doc.date), "MMM dd, yyyy")}
                                </p>
                              </div>
                            </div>
                            <Button variant="ghost" size="sm">
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>


            </>
          )}
        </SheetContent>
      </Sheet>


      {/* ── Floating Quick Actions Toolbar ─────────────────────────────────── */}
      {selectedDisputeIds.length > 0 && (() => {
        const selectedObjs = selectedDisputeIds
          .map(id => filteredDisputes.find(d => d.id === id))
          .filter((d): d is DisputeUI => d !== undefined);
        const totalAmount = selectedObjs.reduce((sum, d) => sum + d.amount, 0);
        return (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-5 duration-300">
            <div className="bg-primary text-primary-foreground rounded-lg shadow-2xl border border-primary/20">
              <div className="flex items-center gap-4 px-6 py-4">

                {/* Selection Info */}
                <div className="flex items-center gap-3 border-r border-primary-foreground/20 pr-4">
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="bg-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/30">
                        {selectedDisputeIds.length}
                      </Badge>
                      <span className="font-medium">
                        {selectedDisputeIds.length === 1 ? "dispute" : "disputes"} selected
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-sm text-primary-foreground/80">
                      <span>
                        Total: <span className="font-semibold">£{totalAmount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  {renderActionsDropdown()}

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setSelectedDisputeIds([]); setSelectedDispute(null) }}
                    className="gap-2 hover:bg-primary-foreground/20"
                  >
                    <X className="h-4 w-4" />
                    Clear
                  </Button>
                </div>

              </div>
            </div>
          </div>
        );
      })()}

      {/* Log Call Modal */}
      <LogCallModal
        open={logCallOpen}
        onOpenChange={setLogCallOpen}
        customerName={selectedDispute?.customer || ""}
        customerPhone={selectedDispute?.contactPhone}
        disputeId={selectedDisputeNumericId ? selectedDisputeNumericId.toString() : undefined}
        onSuccess={() => {
          // Toast is already shown by the modal's internal handleSave
        }}
      />

      {/* Send Email Modal */}
      <SendEmailModal
        open={sendEmailOpen}
        onOpenChange={setSendEmailOpen}
        customerName={selectedDispute?.customer || ""}
        customerEmail={selectedDispute?.contactEmail || ""}
        customerId={selectedDispute?.customerId?.toString()}
        sourceType="dispute"
        sourceId={selectedDisputeNumericId ? selectedDisputeNumericId.toString() : undefined}
        onSuccess={() => {
          // Log communication when email is sent
          const newCommunication: CollectionCommunication = {
            id: `comm-${Date.now()}`,
            caseId: selectedDispute?.id || "",
            type: "email",
            direction: "outbound",
            subject: "Dispute Communication",
            content: "Email sent to customer",
            sentBy: "Current User",
            sentAt: new Date(),
            status: "sent",
          };
          setCommunications([newCommunication, ...communications]);
        }}
      />

      {/* ── Add Note Modal ─────────────────────────────────────────────────── */}
      <AddNoteModal
        open={addNoteOpen}
        onOpenChange={setAddNoteOpen}
        disputeId={selectedDispute?.id || ""}
        disputeLabel={selectedDispute?.id || ""}
        customerName={selectedDispute?.customer || ""}
        onSubmit={handleQuickAddNote}
        isPending={addDisputeNoteMutation.isPending}
      />

      {/* ── Reassign User Modal ─────────────────────────────────────────────── */}
      <Dialog open={reassignOpen} onOpenChange={(o) => { setReassignOpen(o); if (!o) setReassignUserId("") }}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="h-5 w-5 text-primary" />
              Reassign User
            </DialogTitle>
            <DialogDescription>
              {selectedDisputeIds.length === 1
                ? `Reassign dispute ${selectedDispute?.id} to a different user`
                : `Reassign ${selectedDisputeIds.length} disputes to a different user`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label>Assign To</Label>
            <Select value={reassignUserId} onValueChange={setReassignUserId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a user..." />
              </SelectTrigger>
              <SelectContent>
                {users.map((user) => (
                  <SelectItem key={user.id} value={user.id.toString()}>
                    {user.firstName} {user.lastName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setReassignOpen(false); setReassignUserId("") }}>Cancel</Button>
            <Button onClick={handleQuickReassign} disabled={!reassignUserId} className="gap-2">
              <UserCog className="h-4 w-4" />
              Reassign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Update Priority Modal ───────────────────────────────────────────── */}
      <UpdatePriorityModal
        open={updatePriorityOpen}
        onOpenChange={(o) => { setUpdatePriorityOpen(o); if (!o) setUpdatePriorityValue("") }}
        selectedCount={selectedDisputeIds.length}
        selectedDisputeId={selectedDispute?.id}
        priorities={disputePriorities}
        value={updatePriorityValue}
        onValueChange={setUpdatePriorityValue}
        onConfirm={handleQuickUpdatePriority}
      />

      {/* ── Resolve Dispute Modal ───────────────────────────────────────────── */}
      <ResolveDisputeModal
        open={resolveDisputeOpen}
        onOpenChange={setResolveDisputeOpen}
        disputeId={selectedDispute?.id}
        customerName={selectedDispute?.customer}
        onResolve={handleQuickResolveDispute}
      />

      {/* ── Close Dispute Modal ─────────────────────────────────────────────── */}
      <Dialog open={closeDisputeOpen} onOpenChange={(o) => { setCloseDisputeOpen(o); if (!o) { setCloseReason('duplicate'); setCloseNote('') } }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Close Dispute</DialogTitle>
            <DialogDescription>
              Close this dispute without completing all workflow steps. This action will mark the dispute as closed and remove it from active views.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="close-reason">Reason for Closing</Label>
              <Select value={closeReason} onValueChange={(value) => setCloseReason(value as 'duplicate' | 'customer_withdrew' | 'invalid_dispute' | 'other')}>
                <SelectTrigger id="close-reason">
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="duplicate">Duplicate Entry</SelectItem>
                  <SelectItem value="customer_withdrew">Customer Withdrew Dispute</SelectItem>
                  <SelectItem value="invalid_dispute">Invalid Dispute</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="close-note">Additional Notes {closeReason === 'other' && <span className="text-destructive">*</span>}</Label>
              <Textarea
                id="close-note"
                placeholder="Provide additional context for closing this dispute..."
                value={closeNote}
                onChange={(e) => setCloseNote(e.target.value)}
                rows={4}
              />
              {closeReason === 'other' && !closeNote && (
                <p className="text-sm text-muted-foreground">Note is required when selecting "Other" as the reason.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCloseDisputeOpen(false)
                setCloseReason('duplicate')
                setCloseNote('')
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleQuickCloseDispute}
              disabled={closeReason === 'other' && !closeNote}
            >
              <XCircle className="mr-2 h-4 w-4" />
              Close Dispute
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Reopen Dispute Confirmation Dialog */}
      {renderReopenDialog()}

      {/* Next Action Modal */}
      <Dialog open={isNextActionModalOpen} onOpenChange={setIsNextActionModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-primary" />
              Proceed to Next Stage
            </DialogTitle>
            <DialogDescription>
              Complete the current stage and advance the dispute workflow
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Next Stage Info */}
            {selectedDispute && (() => {
              const workflowConfig = getWorkflowForDisputeType(selectedDispute.type);
              const nextStage = workflowConfig.stages.find(s => s.level === selectedDispute.currentLevel + 1);
              
              return nextStage ? (
                <div className="rounded-lg bg-blue-50 dark:bg-blue-950 p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <Zap className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    <p className="font-semibold text-sm text-blue-900 dark:text-blue-100">
                      {nextStage.title}
                    </p>
                  </div>
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    {nextStage.description}
                  </p>
                  {Boolean(nextStage.estimatedDays) && (
                    <p className="text-xs text-blue-600 dark:text-blue-400">
                      Estimated time: {nextStage.estimatedDays} day{nextStage.estimatedDays !== 1 ? 's' : ''}
                    </p>
                  )}
                </div>
              ) : null;
            })()}

            {/* Action Notes */}
            <div className="space-y-2">
              <Label htmlFor="action-notes" className="flex items-center gap-1">
                Action Notes{" "}
                <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="action-notes"
                placeholder="Enter notes about the actions taken and any relevant details..."
                value={nextActionNotes}
                onChange={(e) => setNextActionNotes(e.target.value)}
                rows={4}
                className="resize-none"
              />
              <p className="text-xs text-muted-foreground">
                These notes will be added to the activity timeline
              </p>
            </div>

            {/* Follow-up Date */}
            <div className="space-y-2">
              <Label htmlFor="follow-up-date">Follow-up Date (Optional)</Label>
              <Input
                id="follow-up-date"
                type="date"
                value={nextActionFollowUpDate}
                onChange={(e) => setNextActionFollowUpDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
              <p className="text-xs text-muted-foreground">
                Set a reminder for the next action
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCancelNextAction}
            >
              Cancel
            </Button>
            <Button
              onClick={handleProceedToNextStage}
              disabled={!nextActionNotes.trim()}
              className="gap-2"
            >
              <CheckCircle2 className="h-4 w-4" />
              Proceed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
